import React, { useEffect, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './sheet';
import { cn } from '@/lib/utils';

const SIZE_CLASSES = {
  md: 'w-full sm:max-w-lg',
  lg: 'w-full sm:max-w-xl md:max-w-2xl lg:max-w-3xl',
};

/**
 * AppDrawer — Shared right-side drawer for create/edit forms.
 *
 * @param {boolean}  open
 * @param {function} onOpenChange
 * @param {string}   title
 * @param {string}   [description]
 * @param {'md'|'lg'} [size='md']
 * @param {boolean}  [isDirty=false]  — prompt before closing when true
 * @param {React.ReactNode} [footer]  — sticky footer (Cancel / Save buttons)
 * @param {string}   [data-testid]
 * @param {React.ReactNode} children  — scrollable body content
 */
const AppDrawer = ({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  isDirty = false,
  footer,
  children,
  ...rest
}) => {
  const prevOpen = useRef(open);

  // Fix: Clean up stale pointer-events on body after drawer close.
  // When opening a Sheet from a DropdownMenu item, Radix's pointer-events
  // bookkeeping can get out of sync, leaving body with pointer-events:none.
  useEffect(() => {
    if (prevOpen.current && !open) {
      // Drawer just closed — run cleanup checks at multiple intervals
      // to catch Radix's delayed pointer-events set (happens ~300ms after close)
      const cleanup = () => {
        if (document.body.style.pointerEvents === 'none') {
          const openDialogs = document.querySelectorAll('[role="dialog"][data-state="open"]');
          if (openDialogs.length === 0) {
            document.body.style.removeProperty('pointer-events');
          }
        }
      };
      const t1 = setTimeout(cleanup, 100);
      const t2 = setTimeout(cleanup, 350);
      const t3 = setTimeout(cleanup, 600);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
    prevOpen.current = open;
  }, [open]);

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && isDirty) {
      if (!window.confirm('You have unsaved changes. Discard them?')) return;
    }
    onOpenChange(nextOpen);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          'flex flex-col overflow-hidden p-0',
          SIZE_CLASSES[size] || SIZE_CLASSES.md,
        )}
        data-testid={rest['data-testid']}
      >
        {/* Fixed header */}
        <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b">
          <SheetHeader>
            <SheetTitle className="text-xl">{title}</SheetTitle>
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {children}
        </div>

        {/* Sticky footer */}
        {footer && (
          <div className="flex-shrink-0 px-6 py-4 border-t bg-background flex justify-end gap-3">
            {footer}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export { AppDrawer };
